import App from 'antd/lib/app';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { API } from '../api';
import { useAppDispatch } from '../state';
import {
  setVideoByIdAction,
  setVideoByIdError,
  setVideoByIdLoading,
} from '../state/video/byIdSlice';
import { setLinkStatAction } from '../state/video/linkStat';
import { Paths } from '../types/common';
import { useSearchParams } from '../utils/useSearchParams';
import { useVideoLinkPathId, useVideoPathId } from './use-video-path-id';

const isDebugVideoPlayerEnabled = () =>
  typeof window !== 'undefined' &&
  window.localStorage.debugVideoPlayer === 'true';

const debugVideoPlayerLog = (...args: unknown[]) => {
  if (isDebugVideoPlayerEnabled()) {
    console.log('[VideoPlayer Debug][useLoadVideoWithStat]', ...args);
  }
};

export const useLoadVideoWithStat: () => [
  () => Promise<void>,
  string | undefined,
  string | undefined,
] = () => {
  const dispatch = useAppDispatch();
  const id = useVideoPathId();
  const linkId = useVideoLinkPathId();
  const loadingRef = useRef(false);
  const navigate = useNavigate();
  const { message } = App.useApp();
  const {
    query: { userId },
  } = useSearchParams();

  const handleLoadVideo = useCallback(async () => {
    if (id && linkId) {
      loadingRef.current = true;
      dispatch(setVideoByIdLoading(true));
      try {
        debugVideoPlayerLog('loading started', { id, linkId, userId });

        const [videoResult, linkStatResult, linkChunksStatResult] =
          await Promise.allSettled([
            API.videos.getById(id),
            API.videos.getLinkStat(linkId, userId as string),
            API.videos.getLinChunkskStat(linkId, userId as string),
          ]);

        debugVideoPlayerLog('loading settled', {
          videoStatus: videoResult.status,
          linkStatStatus: linkStatResult.status,
          linkChunksStatStatus: linkChunksStatResult.status,
        });

        if (videoResult.status === 'rejected') {
          throw videoResult.reason;
        }

        dispatch(
          setVideoByIdAction({
            videoData: videoResult.value.data,
          })
        );

        if (linkStatResult.status === 'fulfilled') {
          if (linkChunksStatResult.status === 'rejected') {
            debugVideoPlayerLog('heat-map loading failed; using empty chunks', {
              reason: linkChunksStatResult.reason,
            });
          }

          dispatch(
            setLinkStatAction({
              stat: linkStatResult.value.data,
              chunks:
                linkChunksStatResult.status === 'fulfilled'
                  ? linkChunksStatResult.value.data
                  : [],
            })
          );
        } else {
          debugVideoPlayerLog('link stats loading failed; keeping video loaded', {
            reason: linkStatResult.reason,
          });
        }

        loadingRef.current = false;
      } catch (e) {
        debugVideoPlayerLog('video loading failed', { error: e });
        dispatch(setVideoByIdError(true));
        dispatch(setVideoByIdLoading(false));
        loadingRef.current = false;
        message.error('Видео не найдено');
        navigate(Paths.VIDEO);
      }
    }
  }, [id, userId, linkId, loadingRef, dispatch]);

  useEffect(() => {
    if (!loadingRef.current) {
      handleLoadVideo();
    }
  }, [loadingRef, handleLoadVideo]);

  return [handleLoadVideo, id, linkId];
};
